import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IAccount } from 'src/app/acct-comps/accounts.interfaces';
import { SessionService } from 'src/app/core/backend-adapter/session.service';
import { DownloadJobsService } from '../../services/download-jobs.service';
import { IExportJob, IExportJobsResponse } from '../../review-management.interfaces';

@Component({
  selector: 'app-export-jobs-dropdown',
  templateUrl: './export-jobs-dropdown.component.html',
  styleUrls: ['./export-jobs-dropdown.component.scss'],
})
export class ExportJobsDropdownComponent implements OnInit, OnDestroy {
  showDownloadJobs = false;
  downloadJobsLoading = false;
  exportJobs: IExportJob[] = [];
  hasNewDownloadJobs = false;

  private accountId: number | null = null;
  private userId: number | null = null;
  private knownExportJobIds = new Set<string>();
  private exportJobsPollSub?: Subscription;
  private ngUnsubscribe$ = new Subject<void>();

  constructor(
    private sessionService: SessionService,
    private downloadJobsService: DownloadJobsService,
    private elementRef: ElementRef
  ) {}

  ngOnInit(): void {
    this.sessionService
      .getSelectedAccount$()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((account: IAccount) => {
        this.accountId = account?._id ?? null;
        this.loadExportJobsIfReady();
      });

    this.sessionService
      .getCurrentUser$()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((user) => {
        this.userId = user?.login?._id ?? null;
        this.loadExportJobsIfReady();
      });

    this.downloadJobsService.exportStarted$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(() => {
        this.downloadJobsLoading = true;
      });

    this.downloadJobsService.jobsRefresh$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(() => {
        this.pollExportJobsIfReady();
      });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.showDownloadJobs = false;
    }
  }

  /**
   * Loads export jobs when both account and user identifiers are available.
   * Prevents premature API calls while session streams are still initializing.
   * @returns None.
   */
  private loadExportJobsIfReady(): void {
    if (!this.accountId || !this.userId) {
      return;
    }
    this.loadExportJobs();
  }

  /**
   * Retrieves the latest export jobs for the dropdown.
   * Highlights the trigger when unseen jobs arrive and clears the list on request failure.
   * @returns None.
   */
  private loadExportJobs(): void {
    this.downloadJobsLoading = true;
    this.downloadJobsService
      .getExportJobs(this.userId as number, this.accountId as number)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(
        (response) => this.applyExportJobsResponse(response),
        () => {
          this.exportJobs = [];
          this.downloadJobsLoading = false;
        }
      );
  }

  /**
   * Polls export jobs until all jobs finish after an export is triggered.
   * Cancels any in-flight poll before starting a new one.
   * @returns None.
   */
  private pollExportJobsIfReady(): void {
    if (!this.accountId || !this.userId) {
      return;
    }
    this.exportJobsPollSub?.unsubscribe();
    this.downloadJobsLoading = true;
    this.exportJobsPollSub = this.downloadJobsService
      .pollExportJobsUntilComplete(this.userId, this.accountId)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(
        (response) => this.applyExportJobsResponse(response),
        () => {
          this.downloadJobsLoading = false;
        }
      );
  }

  /**
   * Applies a jobs response to the dropdown state.
   * Flags newly arrived jobs when the dropdown is closed and replaces the displayed job list.
   * @param response Export-jobs response from a single fetch or a poll cycle.
   * @returns None.
   */
  private applyExportJobsResponse(response: IExportJobsResponse): void {
    const incomingJobs = response?.jobs || [];
    const incomingJobIds = new Set(incomingJobs.map((job) => job.job_id));
    const hasUnseenJobs =
      this.knownExportJobIds.size > 0 &&
      incomingJobs.some((job) => job?.job_id && !this.knownExportJobIds.has(job.job_id));

    if (hasUnseenJobs && !this.showDownloadJobs) {
      this.hasNewDownloadJobs = true;
    }

    this.knownExportJobIds = incomingJobIds;
    this.exportJobs = incomingJobs;
    this.downloadJobsLoading = false;
  }

  /**
   * Toggles the visibility of the export-jobs dropdown.
   * Opening the dropdown clears the new-jobs highlight state.
   * @param event Optional click event from the trigger area.
   * @returns None.
   */
  toggleDownloadJobsDropdown(event?: MouseEvent): void {
    event?.stopPropagation();
    this.showDownloadJobs = !this.showDownloadJobs;
    if (this.showDownloadJobs) {
      this.hasNewDownloadJobs = false;
    }
  }

  /**
   * Determines whether an export-job link should be disabled in the dropdown.
   * @param job Export job entry returned by the jobs API.
   * @returns `true` when the link should be shown as disabled; otherwise `false`.
   */
  isJobLinkDisabled(job: IExportJob): boolean {
    return !job?.download_url || !job?.download_url_expires_seconds || job.download_url_expires_seconds === 0;
  }

  /**
   * Formats an export-job timestamp for dropdown display.
   * Prefers `created_at` and falls back to `updated_at`; invalid dates return `job_id`.
   * @param job Export job entry that contains ISO timestamp fields and job metadata.
   * @returns A `mm-dd-yyyy h:min` formatted string, or `job_id` if no valid timestamp is available.
   */
  formatExportJobDate(job: IExportJob): string {
    const rawDate = job?.created_at || job?.updated_at;
    if (!rawDate) {
      return job?.job_id || '';
    }

    const parsedDate = new Date(rawDate);
    if (Number.isNaN(parsedDate.getTime())) {
      return job?.job_id || '';
    }

    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');
    const year = parsedDate.getFullYear();
    const hour = parsedDate.getHours();
    const minute = String(parsedDate.getMinutes()).padStart(2, '0');
    return `${month}-${day}-${year} ${hour}:${minute}`;
  }

  ngOnDestroy(): void {
    this.exportJobsPollSub?.unsubscribe();
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
